import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";
import "./AdminStudentsPage.css";

type Student = {
  fullName: string;
  birthDate: string;
  specialty: string;
  username: string;
  password: string;
  role: string;
  assignmentType?: "test" | "task";
  accessEnabled?: boolean;
  protocolMode?: "dynamic" | "template" | "";
  importId?: string;
  importDate?: string;
  importFileName?: string;
};

type AssignmentType = "test" | "task";
type ProtocolMode = "dynamic" | "template";

type SpecialtyOption = {
  name: string;
  normalized: string;
  source: "question_bank" | "task_bank";
};

type ImportBatch = {
  importId: string;
  importDate: string;
  importFileName: string;
  specialty: string;
  assignmentType: AssignmentType;
  studentsCount: number;
  studentsWithoutResultsCount: number;
  studentsWithResultsCount: number;
  students?: Student[];
};

type ExamSession = {
  sessionId: string;
  name: string;
  specialty: string;
  assignmentType: AssignmentType;
  createdAt: string;
  studentsCount?: number;
};

const assignmentLabels: Record<AssignmentType, string> = {
  test: "Тестирование",
  task: "Ситуационные задачи",
};

const protocolModeLabels: Record<ProtocolMode, string> = {
  dynamic: "Заполненный",
  template: "PDF-шаблон",
};

const getAssignmentType = (student: Student): AssignmentType =>
  student.assignmentType === "task" || student.role === "task" ? "task" : "test";

const getProtocolMode = (student: Student): ProtocolMode =>
  student.protocolMode === "template" ? "template" : "dynamic";

const hasAssignedProtocolMode = (student: Student) =>
  student.accessEnabled === true || Boolean(student.protocolMode);

const putStudentFirst = (studentList: Student[], username: string) => {
  if (!username) {
    return studentList;
  }

  const student = studentList.find((item) => item.username === username);

  return student
    ? [
        student,
        ...studentList.filter((item) => item.username !== username),
      ]
    : studentList;
};

const formatBirthDateInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  return [day, month, year].filter(Boolean).join(".");
};

const AdminStudentsPage: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [importSpecialty, setImportSpecialty] = useState("");
  const [assignmentType, setAssignmentType] = useState<AssignmentType>("test");
  const [protocolMode, setProtocolMode] = useState<ProtocolMode>("dynamic");
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [sessionMode, setSessionMode] = useState<"none" | "existing" | "new">("none");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [newSessionName, setNewSessionName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
  const [manualFullName, setManualFullName] = useState("");
  const [manualBirthDate, setManualBirthDate] = useState("");
  const [manualSpecialty, setManualSpecialty] = useState("");
  const [isAddingManualStudent, setIsAddingManualStudent] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [isGeneratingPasswords, setIsGeneratingPasswords] = useState(false);
  const [isClearingStudents, setIsClearingStudents] = useState(false);
  const [generatedPasswordUsernames, setGeneratedPasswordUsernames] = useState<string[]>([]);
  const [generatedPasswordCount, setGeneratedPasswordCount] = useState(0);
  const [manualAddedUsername, setManualAddedUsername] = useState("");
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [importBatches, setImportBatches] = useState<ImportBatch[]>([]);
  const [selectedImportBatchId, setSelectedImportBatchId] = useState("");
  const [isDeletingImportBatch, setIsDeletingImportBatch] = useState("");
  const [isDownloadingImportBatch, setIsDownloadingImportBatch] = useState("");
  const tableRef = useRef<HTMLTableElement | null>(null);

  const selectedImportBatch = importBatches.find(
    (batch) => batch.importId === selectedImportBatchId
  );
  const visibleStudents = selectedImportBatchId
    ? selectedImportBatch?.students || []
    : students;

  const loadImportBatches = useCallback(() => {
    fetch("/api/student-import-batches")
      .then((res) => res.json())
      .then((data: ImportBatch[]) => {
        const batches = Array.isArray(data) ? data : [];

        setImportBatches(batches);
        setSelectedImportBatchId((current) =>
          current && batches.some((batch) => batch.importId === current)
            ? current
            : ""
        );
      })
      .catch((err) => console.error("IMPORT BATCHES LOAD ERROR", err));
  }, []);

  const loadExamSessions = useCallback(() => {
    fetch("/api/exam-sessions")
      .then((res) => res.json())
      .then((data: ExamSession[]) => {
        const sessions = Array.isArray(data) ? data : [];
        setExamSessions(sessions);
        setSelectedSessionId((current) =>
          current && sessions.some((session) => session.sessionId === current)
            ? current
            : ""
        );
      })
      .catch((err) => console.error("EXAM SESSIONS LOAD ERROR", err));
  }, []);

  const loadStudents = () => {
    fetch("/api/students")
      .then((res) => res.json())
      .then((data: Student[]) => {
        setStudents(data);
        setGeneratedPasswordUsernames([]);
        setGeneratedPasswordCount(0);
        setManualAddedUsername("");
        setLastSelectedIndex(null);
        setSelectedStudents((selected) =>
          selected.filter((username) =>
            data.some((student) => student.username === username)
          )
        );
      })
      .catch((err) => console.error("Ошибка загрузки студентов:", err));
  };

  const loadSpecialties = useCallback(() => {
    fetch(`/api/specialties?assignmentType=${assignmentType}`)
      .then((res) => res.json())
      .then((data: Array<SpecialtyOption | string>) => {
        const nextSpecialties = Array.isArray(data)
          ? data
              .map((specialty) =>
                typeof specialty === "string"
                  ? {
                      name: specialty,
                      normalized: specialty,
                      source:
                        assignmentType === "task"
                          ? ("task_bank" as const)
                          : ("question_bank" as const),
                    }
                  : specialty
              )
              .filter((specialty) => specialty.name)
          : [];

        setSpecialties(nextSpecialties);
        setManualSpecialty((current) =>
          nextSpecialties.some((specialty) => specialty.name === current)
            ? current
            : ""
        );
      })
      .catch((err) => console.error("SPECIALTIES LOAD ERROR", err));
  }, [assignmentType]);

  useEffect(() => {
    loadStudents();
    loadImportBatches();
    loadExamSessions();
  }, []);

  useEffect(() => {
    loadSpecialties();
  }, [loadSpecialties]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    console.log("FILE SELECTED", event.target.files);

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("specialty", importSpecialty);
    formData.append("assignmentType", assignmentType);

    setIsImporting(true);
    setImportMessage("");

    try {
      console.log("SENDING IMPORT REQUEST");

      const response = await fetch("/api/import-students", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      console.log("IMPORT RESPONSE", data);

      if (!response.ok) {
        throw new Error(data.details || data.error || "Ошибка загрузки Excel");
      }

      const skippedCount = data.skippedStudents?.length || 0;
      setStudents(data.students || []);
      setSelectedStudents([]);
      setLastSelectedIndex(null);
      setGeneratedPasswordUsernames([]);
      setGeneratedPasswordCount(0);
      setManualAddedUsername("");
      setSelectedImportBatchId("");
      loadImportBatches();
      loadSpecialties();
      setImportMessage(
        `Загружено студентов: ${data.count}${
          skippedCount ? `, пропущено: ${skippedCount}` : ""
        }`
      );

      loadStudents();
    } catch (err) {
      console.error("IMPORT ERROR", err);
      const message =
        err instanceof Error ? err.message : "Ошибка загрузки Excel";
      setImportMessage(message);
    } finally {
      setIsImporting(false);
      event.target.value = "";
    }
  };

  const handleExportStudents = async () => {
    try {
      const response = await fetch("/api/export-students");

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.error || "Сначала сгенерируйте пароли выбранным студентам"
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "students_credentials.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Сначала сгенерируйте пароли выбранным студентам";
      setImportMessage(message);
    }
  };

  const handleClearCurrentStudents = async () => {
    const confirmed = window.confirm(
      "Очистить текущий список студентов? Уже созданные пользователи в storage/users.json удалены не будут."
    );

    if (!confirmed) {
      return;
    }

    setIsClearingStudents(true);
    setImportMessage("");

    try {
      const response = await fetch("/api/students/clear-current", {
        method: "POST",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось очистить текущий список");
      }

      setStudents([]);
      setSelectedStudents([]);
      setLastSelectedIndex(null);
      setGeneratedPasswordUsernames([]);
      setGeneratedPasswordCount(0);
      setManualAddedUsername("");
      setImportMessage("Текущий список очищен");
    } catch (err) {
      console.error("CLEAR STUDENTS ERROR", err);
      const message =
        err instanceof Error ? err.message : "Не удалось очистить текущий список";
      setImportMessage(message);
    } finally {
      setIsClearingStudents(false);
    }
  };

  const handleSelectStudent = (
    username: string,
    checked: boolean,
    index: number,
    event?: MouseEvent | React.MouseEvent
  ) => {
    const orderedUsernames = visibleStudents.map((student) => student.username);

    if (event?.shiftKey && lastSelectedIndex !== null) {
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      const rangeUsernames = visibleStudents
        .slice(startIndex, endIndex + 1)
        .map((student) => student.username);

      setSelectedStudents((selected) => {
        const nextSelected = new Set(selected);

        rangeUsernames.forEach((rangeUsername) => {
          if (checked) {
            nextSelected.add(rangeUsername);
          } else {
            nextSelected.delete(rangeUsername);
          }
        });

        return orderedUsernames.filter((orderedUsername) =>
          nextSelected.has(orderedUsername)
        );
      });
    } else {
      setSelectedStudents((selected) => {
        const nextSelected = new Set(selected);

        if (checked) {
          nextSelected.add(username);
        } else {
          nextSelected.delete(username);
        }

        return orderedUsernames.filter((orderedUsername) =>
          nextSelected.has(orderedUsername)
        );
      });
    }

    setLastSelectedIndex(index);
  };

  const handleSelectAllStudents = (checked: boolean) => {
    setSelectedStudents(checked ? visibleStudents.map((student) => student.username) : []);
    setLastSelectedIndex(
      checked && visibleStudents.length ? visibleStudents.length - 1 : null
    );
  };

  const handleClearSelection = () => {
    setSelectedStudents([]);
    setLastSelectedIndex(null);
  };

  const handleAssignmentTypeChange = (value: AssignmentType) => {
    setAssignmentType(value);
    setGeneratedPasswordUsernames([]);
    setGeneratedPasswordCount(0);
    setManualAddedUsername("");
  };

  const handleManualAddStudent = async () => {
    if (!manualFullName.trim()) {
      setImportMessage("Укажите ФИО студента");
      return;
    }

    if (!manualSpecialty.trim()) {
      setImportMessage("Выберите специальность");
      return;
    }

    setIsAddingManualStudent(true);
    setImportMessage("");

    try {
      const response = await fetch("/api/students/manual", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: manualFullName,
          birthDate: manualBirthDate,
          specialty: manualSpecialty,
          assignmentType,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось добавить студента");
      }

      const newStudent = data.student as Student | undefined;

      if (newStudent?.username) {
        setStudents(data.students || []);
        setSelectedStudents([newStudent.username]);
        const newStudentIndex = Array.isArray(data.students)
          ? data.students.findIndex(
              (student: Student) => student.username === newStudent.username
            )
          : -1;
        setLastSelectedIndex(newStudentIndex >= 0 ? newStudentIndex : null);
        setManualAddedUsername(newStudent.username);
      } else {
        setStudents(data.students || []);
        setSelectedStudents([]);
        setLastSelectedIndex(null);
        setManualAddedUsername("");
      }
      setGeneratedPasswordUsernames([]);
      setGeneratedPasswordCount(0);
      setManualFullName("");
      setManualBirthDate("");
      setManualSpecialty("");
      setImportMessage("Студент добавлен в текущую группу");
      loadSpecialties();
      window.requestAnimationFrame(() => {
        tableRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      });
    } catch (err) {
      console.error("MANUAL STUDENT ADD ERROR", err);
      const message =
        err instanceof Error ? err.message : "Не удалось добавить студента";
      setImportMessage(message);
    } finally {
      setIsAddingManualStudent(false);
    }
  };

  const handleGeneratePasswords = async () => {
    if (sessionMode === "existing" && !selectedSessionId) {
      setImportMessage("Выберите экзаменационный поток");
      return;
    }

    if (sessionMode === "new" && !newSessionName.trim()) {
      setImportMessage("Укажите название нового потока");
      return;
    }

    if (selectedStudents.length === 0) {
      setImportMessage("Выберите студентов");
      return;
    }

    setIsGeneratingPasswords(true);
    setImportMessage("");
    setGeneratedPasswordUsernames([]);
    setGeneratedPasswordCount(0);
    const usernamesForGeneration = [...selectedStudents];

    try {
      const response = await fetch(
        "/api/students/regenerate-passwords",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            usernames: usernamesForGeneration,
            assignmentType,
            protocolMode,
            sessionMode,
            sessionId: selectedSessionId,
            sessionName: newSessionName,
            sessionSpecialty:
              visibleStudents.find((student) =>
                selectedStudents.includes(student.username)
              )?.specialty || importSpecialty || manualSpecialty,
          }),
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось сгенерировать пароли");
      }

      setStudents(data.students || []);
      setSelectedStudents([]);
      setLastSelectedIndex(null);
      setGeneratedPasswordUsernames(
        Array.isArray(data.generatedStudents)
          ? data.generatedStudents
              .map((student: Student) => student.username)
              .filter(Boolean)
          : usernamesForGeneration.filter((username) =>
              (data.students || []).some(
                (student: Student) => student.username === username
              )
            )
      );
      setGeneratedPasswordCount(data.count || 0);
      if (data.sessionId) {
        setSelectedSessionId(data.sessionId);
        setSessionMode("existing");
        setNewSessionName("");
      }
      loadExamSessions();
      setImportMessage(
        `Обновлено паролей: ${data.count}. Ведомость сохранена в историю`
      );
    } catch (err) {
      console.error("PASSWORD GENERATION ERROR", err);
      const message =
        err instanceof Error ? err.message : "Не удалось сгенерировать пароли";
      setImportMessage(message);
    } finally {
      setIsGeneratingPasswords(false);
    }
  };

  const handleOpenImportBatch = (importId: string) => {
    setSelectedImportBatchId(importId);
    setSelectedStudents([]);
    setLastSelectedIndex(null);
    window.requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  };

  const handleShowAllStudents = () => {
    setSelectedImportBatchId("");
    setSelectedStudents([]);
    setLastSelectedIndex(null);
  };

  const handleDownloadImportBatch = async (batch: ImportBatch) => {
    setIsDownloadingImportBatch(batch.importId);
    setImportMessage("");

    try {
      const response = await fetch(
        `/api/student-import-batches/${encodeURIComponent(batch.importId)}/download`
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Не удалось скачать партию импорта");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const encodedFileName = disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1];
      const quotedFileName = disposition.match(/filename="?([^";]+)"?/)?.[1];
      const fileName = encodedFileName
        ? decodeURIComponent(encodedFileName)
        : quotedFileName || `${batch.importId}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Не удалось скачать партию импорта";
      setImportMessage(message);
    } finally {
      setIsDownloadingImportBatch("");
    }
  };

  const handleDeleteImportBatch = async (batch: ImportBatch) => {
    const confirmed = window.confirm(
      "Удалить студентов из этой партии? Студенты с результатами удалены не будут."
    );

    if (!confirmed) {
      return;
    }

    setIsDeletingImportBatch(batch.importId);
    setImportMessage("");

    try {
      const response = await fetch(
        `/api/student-import-batches/${encodeURIComponent(batch.importId)}`,
        { method: "DELETE" }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить партию импорта");
      }

      setImportMessage(
        `Удалено студентов: ${data.deletedCount}. Пропущено с результатами: ${data.skippedWithResultsCount}`
      );
      setSelectedStudents([]);
      setLastSelectedIndex(null);
      if (selectedImportBatchId === batch.importId) {
        setSelectedImportBatchId("");
      }
      loadStudents();
      loadImportBatches();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Не удалось удалить партию импорта";
      setImportMessage(message);
    } finally {
      setIsDeletingImportBatch("");
    }
  };

  const emptySpecialtiesMessage =
    assignmentType === "task"
      ? "Нет загруженных баз ситуационных задач"
      : "Нет загруженных баз вопросов";
  const canAddManualStudent =
    Boolean(manualFullName.trim()) && Boolean(manualSpecialty.trim());

  const allStudentsSelected =
    visibleStudents.length > 0 &&
    visibleStudents.every((student) => selectedStudents.includes(student.username));
  const generatedPasswordUsernameSet = new Set(generatedPasswordUsernames);
  return (
    <div className="admin-page">
      <div className="admin-page-inner">
      <AdminNav section="students" />

      <h2>Загрузка студентов</h2>

      <div
        className="admin-card admin-toolbar"
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "16px",
        }}
      >
        <span>Назначение:</span>
        <button
          type="button"
          onClick={() => handleAssignmentTypeChange("test")}
          style={{
            padding: "8px 12px",
            border: "1px solid #777",
            backgroundColor: assignmentType === "test" ? "#e6f4f1" : "#fff",
            fontWeight: assignmentType === "test" ? 700 : 400,
          }}
        >
          Тестирование
        </button>
        <button
          type="button"
          onClick={() => handleAssignmentTypeChange("task")}
          style={{
            padding: "8px 12px",
            border: "1px solid #777",
            backgroundColor: assignmentType === "task" ? "#e6f4f1" : "#fff",
            fontWeight: assignmentType === "task" ? 700 : 400,
          }}
        >
          Ситуационные задачи
        </button>
      </div>

      <div
        className="admin-card admin-toolbar"
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Специальность"
          value={importSpecialty}
          onChange={(event) => setImportSpecialty(event.target.value)}
          style={{ padding: "8px 12px", minWidth: "260px" }}
        />

        <label
          style={{
            border: "1px solid #777",
            padding: "8px 12px",
            cursor: isImporting ? "default" : "pointer",
            backgroundColor: isImporting ? "#eee" : "#fff",
          }}
        >
          Выбрать Excel
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={isImporting}
            style={{ display: "none" }}
          />
        </label>

        <button
          onClick={handleGeneratePasswords}
          disabled={selectedStudents.length === 0 || isGeneratingPasswords}
        >
          Сгенерировать новые пароли выбранным
        </button>

        <div className="protocol-mode-picker" aria-label="Протокол после завершения">
          <span>Протокол после завершения:</span>
          <label>
            <input
              type="radio"
              name="protocolMode"
              value="dynamic"
              checked={protocolMode === "dynamic"}
              onChange={() => setProtocolMode("dynamic")}
            />
            Заполненный протокол
          </label>
          <label>
            <input
              type="radio"
              name="protocolMode"
              value="template"
              checked={protocolMode === "template"}
              onChange={() => setProtocolMode("template")}
            />
            PDF-шаблон
          </label>
        </div>

        <div className="protocol-mode-picker" aria-label="Экзаменационная сессия">
          <span>Экзаменационная сессия / поток:</span>
          <select
            value={sessionMode}
            onChange={(event) =>
              setSessionMode(event.target.value as "none" | "existing" | "new")
            }
          >
            <option value="none">Без потока</option>
            <option value="new">Создать новый поток</option>
            <option value="existing">Выбрать существующий поток</option>
          </select>
          {sessionMode === "existing" && (
            <select
              value={selectedSessionId}
              onChange={(event) => setSelectedSessionId(event.target.value)}
            >
              <option value="">Выберите поток</option>
              {examSessions.map((session) => (
                <option key={session.sessionId} value={session.sessionId}>
                  {session.name}
                </option>
              ))}
            </select>
          )}
          {sessionMode === "new" && (
            <input
              type="text"
              placeholder="Название нового потока"
              value={newSessionName}
              onChange={(event) => setNewSessionName(event.target.value)}
              style={{ minWidth: "240px" }}
            />
          )}
        </div>

        <button onClick={handleExportStudents} disabled={students.length === 0}>
          Скачать ведомость
        </button>

        <button
          className="admin-button admin-button-danger"
          type="button"
          onClick={handleClearCurrentStudents}
          disabled={students.length === 0 || isClearingStudents}
        >
          Очистить список
        </button>

        {importMessage && <span>{importMessage}</span>}
      </div>

      <div
        className="admin-card"
        style={{
          border: "1px solid #ddd",
          padding: "12px",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Ручное добавление студента</h3>
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="ФИО"
            value={manualFullName}
            onChange={(event) => setManualFullName(event.target.value)}
            style={{ padding: "8px 12px", minWidth: "260px" }}
          />

          <select
            value={manualSpecialty}
            onChange={(event) => setManualSpecialty(event.target.value)}
            disabled={specialties.length === 0}
            style={{ padding: "8px 12px", minWidth: "260px" }}
          >
            {specialties.length === 0 ? (
              <option value="">{emptySpecialtiesMessage}</option>
            ) : (
              <>
                <option value="">Выберите специальность</option>
                {specialties.map((specialty) => (
                  <option
                    key={`${specialty.source}-${specialty.normalized}`}
                    value={specialty.name}
                  >
                    {specialty.name}
                  </option>
                ))}
              </>
            )}
          </select>

          <input
            type="text"
            placeholder="Дата рождения"
            value={manualBirthDate}
            onChange={(event) =>
              setManualBirthDate(formatBirthDateInput(event.target.value))
            }
            inputMode="numeric"
            maxLength={10}
            style={{ padding: "8px 12px", minWidth: "160px" }}
          />

          <span>Назначение: {assignmentLabels[assignmentType]}</span>

          <button
            type="button"
            onClick={handleManualAddStudent}
            disabled={isAddingManualStudent || !canAddManualStudent}
          >
            Добавить в список
          </button>
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: "16px", marginBottom: "16px" }}>
        <h3 style={{ marginTop: 0 }}>Партии импорта</h3>
        {selectedImportBatch && (
          <div className="admin-message" style={{ marginBottom: "12px" }}>
            Открыта партия: {selectedImportBatch.importFileName || selectedImportBatch.importId}
            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={handleShowAllStudents}
              style={{ marginLeft: "12px" }}
            >
              Показать всех студентов
            </button>
          </div>
        )}
        <div className="admin-table-wrap">
          <table className="admin-table import-batches-table">
            <thead>
              <tr>
                <th>Дата импорта</th>
                <th>Файл</th>
                <th>Специальность</th>
                <th>Назначение</th>
                <th>Всего</th>
                <th>Без результатов</th>
                <th>С результатами</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {importBatches.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-table-empty">
                    Нет партий импорта
                  </td>
                </tr>
              ) : (
                importBatches.map((batch) => (
                  <tr key={batch.importId}>
                    <td>
                      {batch.importDate
                        ? new Date(batch.importDate).toLocaleString("ru-RU")
                        : "—"}
                    </td>
                    <td>{batch.importFileName || "—"}</td>
                    <td>{batch.specialty || "—"}</td>
                    <td>{assignmentLabels[batch.assignmentType]}</td>
                    <td>{batch.studentsCount}</td>
                    <td>{batch.studentsWithoutResultsCount}</td>
                    <td>{batch.studentsWithResultsCount}</td>
                    <td>
                      <div className="import-batch-actions">
                        <button
                          type="button"
                          className="admin-button admin-button-secondary"
                          onClick={() => handleOpenImportBatch(batch.importId)}
                        >
                          Открыть
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button-secondary import-batch-download-button"
                          onClick={() => handleDownloadImportBatch(batch)}
                          disabled={isDownloadingImportBatch === batch.importId}
                        >
                          {isDownloadingImportBatch === batch.importId
                            ? "Скачивание..."
                            : "Скачать"}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button-danger"
                          onClick={() => handleDeleteImportBatch(batch)}
                          disabled={
                            batch.studentsWithoutResultsCount === 0 ||
                            isDeletingImportBatch === batch.importId
                          }
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: "20px", marginBottom: "8px" }}>
        <strong>Всего студентов:</strong> {students.length}{" "}
        <strong style={{ marginLeft: "20px" }}>Выбрано:</strong>{" "}
        {selectedStudents.length}
      </div>

      <div className="student-selection-tools">
        <div className="student-selection-actions">
          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={() => handleSelectAllStudents(true)}
            disabled={visibleStudents.length === 0}
          >
            Выделить всех
          </button>
          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={handleClearSelection}
            disabled={selectedStudents.length === 0}
          >
            Снять выделение
          </button>
        </div>
        <div className="student-selection-hint">
          Shift — выбрать диапазон студентов. Ctrl — выбрать отдельных студентов.
        </div>
      </div>

      {generatedPasswordCount > 0 && (
        <div className="generated-passwords-message">
          Сгенерировано паролей: {generatedPasswordCount}
        </div>
      )}

      <div className="admin-table-wrap">
      <table
        className="admin-table"
        ref={tableRef}
        border={1}
        cellPadding={8}
        style={{
          borderCollapse: "collapse",
          width: "100%",
          marginTop: "8px",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "55px" }}>№</th>
            <th style={{ width: "40px" }}>
              <input
                type="checkbox"
                checked={allStudentsSelected}
                onChange={(event) => handleSelectAllStudents(event.target.checked)}
                disabled={students.length === 0}
              />
            </th>
            <th>ФИО</th>
            <th>Дата рождения</th>
            <th>Специальность</th>
            <th>Назначение</th>
            <th>Логин</th>
            <th>Пароль</th>
            <th>Протокол</th>
          </tr>
        </thead>

        <tbody>
          {visibleStudents.length === 0 ? (
            <tr>
              <td colSpan={9} className="admin-table-empty">
                Нет загруженной группы студентов
              </td>
            </tr>
          ) : (
            visibleStudents.map((student, index) => (
              <tr
                key={`${student.username}-${index}`}
                className={
                  [
                    generatedPasswordUsernameSet.has(student.username)
                      ? "generated-row generated-password-row"
                      : "",
                    manualAddedUsername === student.username ? "manual-added-row" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              >
                <td style={{ textAlign: "center", width: "55px" }}>{index + 1}</td>
                <td
                  style={{ textAlign: "center", width: "40px", cursor: "pointer" }}
                  onClick={(event) =>
                    handleSelectStudent(
                      student.username,
                      !selectedStudents.includes(student.username),
                      index,
                      event
                    )
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.username)}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelectStudent(
                        student.username,
                        event.currentTarget.checked,
                        index,
                        event
                      );
                    }}
                    onChange={() => undefined}
                  />
                </td>
                <td>{student.fullName}</td>
                <td>{student.birthDate}</td>
                <td>{student.specialty}</td>
                <td>
                  <span
                    className={`admin-badge admin-badge-${getAssignmentType(student)}`}
                  >
                    {assignmentLabels[getAssignmentType(student)]}
                  </span>
                </td>
                <td>{student.username}</td>
                <td>{student.password}</td>
                <td>
                  {hasAssignedProtocolMode(student) ? (
                    <span
                      className={`admin-badge protocol-mode-badge protocol-mode-badge-${getProtocolMode(student)}`}
                    >
                      {protocolModeLabels[getProtocolMode(student)]}
                    </span>
                  ) : (
                    <span className="admin-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      </div>
    </div>
  );
};

export default AdminStudentsPage;
